import { makeAutoObservable } from 'mobx';
import { cloneDeep, isEmpty } from 'lodash-es';
import type { FilterStoreType, RuleFilter } from '../../types';

class FacebookFilterStore implements FilterStoreType {
  filterGroups: RuleFilter[][] = [];

  constructor() {
    makeAutoObservable(this);
  }

  setFilterGroups(filterGroups: RuleFilter[][]) {
    this.filterGroups = filterGroups;
  }

  reset() {
    this.filterGroups = [];
  }

  updateField(groupIndex: number, filterIndex: number, newValue: string) {
    const filter = this.filterGroups[groupIndex][filterIndex];

    filter.value = '';
    filter.operator = '';
    filter.field = newValue;
  }

  updateOperator(groupIndex: number, filterIndex: number, newValue: string) {
    try {
      this.filterGroups[groupIndex][filterIndex].operator = newValue;
    } catch (e) {
      console.error(e);
    }
  }

  updateValue(groupIndex: number, filterIndex: number, newValue: string | string[]) {
    try {
      this.filterGroups[groupIndex][filterIndex].value = newValue;
    } catch (e) {
      console.error(e);
    }
  }

  getFilter(groupIndex: number, filterIndex: number): RuleFilter | null {
    if (isEmpty(this.filterGroups)) return null;
    return this.filterGroups[groupIndex][filterIndex];
  }

  addFilter(groupIndex: number) {
    const isLastFilterValid = this.validateLastFilter(groupIndex);
    if (isLastFilterValid) {
      this.filterGroups[groupIndex].push({ field: '', operator: '', value: '' });
    }
    return isLastFilterValid;
  }

  removeFilterGroup(groupIndex: number): boolean {
    if (this.filterGroups.length > 1) {
      this.filterGroups = this.filterGroups.filter((fg, i) => i !== groupIndex);
      return true;
    }
    return false;
  }

  removeFilter(groupIndex: number, index: number): boolean {
    const newFilters = this.filterGroups[groupIndex].filter((fg, i) => i !== index);
    if (newFilters.length) {
      this.filterGroups[groupIndex] = this.filterGroups[groupIndex].filter((fg, i) => i !== index);
      return true;
    } else {
      return this.removeFilterGroup(groupIndex);
    }
  }

  addFilterGroup() {
    this.filterGroups.push([{ field: '', operator: '', value: '' }]);
  }

  validateLastFilter(groupIndex: number) {
    const group = this.filterGroups[groupIndex];
    return Object.values(group[group.length - 1]).every((v) => !!v);
  }

  duplicateFilterGroup(groupIndex: number) {
    this.filterGroups.push(cloneDeep(this.filterGroups[groupIndex]));
  }
}

const facebookFilterStore = new FacebookFilterStore();

export default facebookFilterStore;
